// src/components/Navbar.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.png';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '../types';

const menuVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const changeLang = async (lng: string) => {
    await i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
  };

  const menuItems: MenuItem[] = [
    { to: '/', label: t('nav.home') },
    { to: '/information', label: t('nav.information') },
    { to: '/cenik', label: t('nav.pricing') },
    { to: '/rezervace', label: t('nav.reservation') },
    { to: '/galerie', label: t('nav.gallery') },
    { to: '/kontakt', label: t('nav.contact') },
  ];

  return (
    <>
      {/* Horní lišta */}
      <nav className="fixed top-0 left-0 w-full z-40 bg-gradient-to-r from-[#F5F1E7] via-[#EAD9B5] to-[#D9C2A0] shadow-xl backdrop-blur-sm border-b border-gray-100 h-16">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-full relative">
          {/* Logo - absolutní pozice vlevo nahoře, přesahuje nahoru */}
          <div className="absolute left-6 top-0 flex-shrink-0" style={{ zIndex: 60 }}>
            <Link to="/">
              <img
                src={logo}
                alt="Chalupa Laura Logo"
                className="h-32 w-auto object-contain cursor-pointer transition-transform duration-300 hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex ml-auto space-x-6 text-base font-semibold text-[#4B2E2E] items-center">
            {menuItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="relative hover:text-[#A9745B] transition-all duration-300 after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-[2px] after:bg-[#A9745B] hover:after:w-full after:transition-all after:duration-300"
              >
                {item.label}
              </Link>
            ))}
            {/* Language switcher */}
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => void changeLang('cs')}
                aria-label={t('language.cs')}
                className={`w-8 h-6 rounded overflow-hidden ring-2 hover:ring-[#4B2E2E] transition ${i18n.language === 'cs' ? 'ring-[#4B2E2E]' : 'ring-gray-300'}`}
                title="Čeština"
              >
                <svg viewBox="0 0 900 600" className="w-full h-full">
                  <rect fill="#d7141a" width="900" height="600" />
                  <rect fill="#fff" width="900" height="300" />
                  <path fill="#11457e" d="M 450,300 0,0 V 600 z" />
                </svg>
              </button>
              <button
                onClick={() => void changeLang('en')}
                aria-label={t('language.en')}
                className={`w-8 h-6 rounded overflow-hidden ring-2 hover:ring-[#4B2E2E] transition ${i18n.language === 'en' ? 'ring-[#4B2E2E]' : 'ring-gray-300'}`}
                title="English"
              >
                <svg viewBox="0 0 60 30" className="w-full h-full">
                  <clipPath id="s"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
                  <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
                  <g clipPath="url(#s)">
                    <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
                    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
                    <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
                    <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
                    <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
                  </g>
                </svg>
              </button>
              <button
                onClick={() => void changeLang('de')}
                aria-label={t('language.de')}
                className={`w-8 h-6 rounded overflow-hidden ring-2 hover:ring-[#4B2E2E] transition ${i18n.language === 'de' ? 'ring-[#4B2E2E]' : 'ring-gray-300'}`}
                title="Deutsch"
              >
                <svg viewBox="0 0 5 3" className="w-full h-full">
                  <rect fill="#000" width="5" height="3" />
                  <rect fill="#D00" y="1" width="5" height="2" />
                  <rect fill="#FFCE00" y="2" width="5" height="1" />
                </svg>
              </button>
            </div>
          </div>

          {/* Burger / X button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen((o) => !o)}
            className="md:hidden focus:outline-none z-50 ml-auto"
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            <svg
              className="h-6 w-6 text-[#4B2E2E] transition-transform duration-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </motion.button>
        </div>
      </nav>

      {/* Mobilní menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed left-0 right-0 top-16 bottom-0 bg-white/90 backdrop-blur-md z-40 flex flex-col text-base font-medium text-[#4B2E2E] px-6 py-4 shadow-2xl border-t border-gray-200"
          >
            {menuItems.map(item => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setIsOpen(false)}
                className="py-2 hover:bg-[#F5F1E7] rounded-md transition-colors duration-300"
              >
                {item.label}
              </Link>
            ))}
            <div className="flex gap-3 pt-2 border-t border-gray-300 mt-2">
              <button
                onClick={() => void changeLang('cs')}
                className={`flex-1 py-2 px-2 rounded-md text-sm font-semibold hover:bg-[#F5F1E7] transition flex items-center justify-center gap-2 ${i18n.language === 'cs' ? 'bg-[#F5F1E7] ring-2 ring-[#4B2E2E]' : ''}`}
              >
                <svg viewBox="0 0 900 600" className="w-6 h-4 rounded">
                  <rect fill="#d7141a" width="900" height="600" />
                  <rect fill="#fff" width="900" height="300" />
                  <path fill="#11457e" d="M 450,300 0,0 V 600 z" />
                </svg>
                {t('language.cs')}
              </button>
              <button
                onClick={() => void changeLang('en')}
                className={`flex-1 py-2 px-2 rounded-md text-sm font-semibold hover:bg-[#F5F1E7] transition flex items-center justify-center gap-2 ${i18n.language === 'en' ? 'bg-[#F5F1E7] ring-2 ring-[#4B2E2E]' : ''}`}
              >
                <svg viewBox="0 0 60 30" className="w-6 h-4 rounded">
                  <clipPath id="s2"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
                  <clipPath id="t2"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
                  <g clipPath="url(#s2)">
                    <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
                    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
                    <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t2)" stroke="#C8102E" strokeWidth="4" />
                    <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
                    <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
                  </g>
                </svg>
                {t('language.en')}
              </button>
              <button
                onClick={() => void changeLang('de')}
                className={`flex-1 py-2 px-2 rounded-md text-sm font-semibold hover:bg-[#F5F1E7] transition flex items-center justify-center gap-2 ${i18n.language === 'de' ? 'bg-[#F5F1E7] ring-2 ring-[#4B2E2E]' : ''}`}
              >
                <svg viewBox="0 0 5 3" className="w-6 h-4 rounded">
                  <rect fill="#000" width="5" height="3" />
                  <rect fill="#D00" y="1" width="5" height="2" />
                  <rect fill="#FFCE00" y="2" width="5" height="1" />
                </svg>
                {t('language.de')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Posunutí obsahu dolů kvůli velkému logu */}
      <div className="pt-24">{/* Obsah stránky */}</div>
    </>
  );
};

export default Navbar;
